import React, { useCallback, useMemo, useState } from 'react';
import { Button, Col, Dropdown, Row, Switch } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { download } from '@helpers/download';
import { useResponsive } from '@hooks/useResponsive';
import { ReactComponent as ActionSvg } from '@assets/icons/action.svg';
import { ReactComponent as DownloadInvoiceSvg } from '@assets/icons/download-invoice.svg';
import { ReactComponent as DeleteSvg } from '@assets/icons/delete.svg';
import { ReactComponent as ScrollVerticalSvg } from '@assets/icons/scroll-vertical.svg';
import { ConfirmModal } from '@components/Modals';
import { deleteTranscriptionAssetVersion } from '@redux/actions/assetsAction';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { useTypedSelector } from '@hooks';

type HeadTitleProps = {
  canModifyTranscription: boolean;
  isAutoScrolling: boolean;
  hasTranscript: boolean;
  availableTranscripts: Record<string, string> | null;
  setIsAutoScrolling: (value: boolean) => void;
};

export default function HeadTitle({
  canModifyTranscription,
  isAutoScrolling,
  hasTranscript,
  availableTranscripts,
  setIsAutoScrolling
}: HeadTitleProps) {
  const dispatch = useTypedDispatch();
  const asset = useTypedSelector((state) => state.mediaViewer.assets);
  const selectedVersion = useTypedSelector(
    (state) => state.mediaViewer.selectedVersion
  );
  const { isMdWidth } = useResponsive();
  const [openKeysSubMenu, setOpenKeysSubMenu] = useState<string[]>([]);
  const [isShowMenu, setIsShowMenu] = useState(false);
  const [isShowDeleteTranscriptionModal, setIsShowDeleteTranscriptionModal] =
    useState(false);

  const onCloseMenu = useCallback(() => {
    setIsShowMenu(false);
    setOpenKeysSubMenu([]);
  }, [setIsShowMenu, setOpenKeysSubMenu]);

  const menuItems = useMemo(() => {
    if (!hasTranscript) return [];
    const items: ItemType[] = [
      {
        label: (
          <Row
            gutter={12}
            justify="space-between"
            onClick={() => {
              setIsAutoScrolling(!isAutoScrolling);
            }}
          >
            <Col>Auto-scrolling</Col>
            <Col>
              <Switch checked={isAutoScrolling} />
            </Col>
          </Row>
        ),
        disabled: true,
        key: 'auto-scrolling',
        className: 'b-allow-action',
        icon: <ScrollVerticalSvg />
      }
    ];
    const transcriptionFiles: ItemType[] = [];
    if (availableTranscripts?.subRipUrl) {
      transcriptionFiles.push({
        key: 'transcription-srt-download',
        label: 'SRT',
        onClick: () => {
          download(availableTranscripts.subRipUrl);
        }
      });
    }
    if (availableTranscripts?.webVttUrl) {
      transcriptionFiles.push({
        key: 'transcription-vtt-download',
        label: 'VVT',
        onClick: () => {
          download(availableTranscripts.webVttUrl);
        }
      });
    }
    if (availableTranscripts?.url) {
      transcriptionFiles.push({
        key: 'transcription-raw-download',
        label: 'RAW',
        onClick: () => {
          download(availableTranscripts.url);
        }
      });
    }
    if (!transcriptionFiles.length) return [];
    if (isMdWidth) {
      transcriptionFiles.push({
        key: 'cancel',
        label: 'Cancel',
        onClick: onCloseMenu
      });
    }
    items.push({
      key: 'transcription-download',
      label: 'Download transcription',
      icon: <DownloadInvoiceSvg className="b-icon-download-invoice" />,
      children: transcriptionFiles,
      popupOffset: [12, 0],
      popupClassName: 'media_viewer_submenu media_viewer_mobile-overlay'
    });
    if (canModifyTranscription) {
      items.push({
        type: 'divider'
      });
      items.push({
        key: 'delete',
        icon: <DeleteSvg className="b-icon-delete" />,
        label: 'Delete',
        onClick: () => {
          setIsShowDeleteTranscriptionModal(true);
        }
      });
    }
    if (isMdWidth) {
      items.push({
        key: 'cancel',
        label: 'Cancel',
        onClick: onCloseMenu
      });
    }
    return items;
  }, [
    isMdWidth,
    hasTranscript,
    canModifyTranscription,
    availableTranscripts,
    isAutoScrolling,
    setIsAutoScrolling,
    onCloseMenu,
    setIsShowDeleteTranscriptionModal
  ]);

  const onDeleteTranscription = () => {
    if (!asset || !selectedVersion) return;
    setIsShowDeleteTranscriptionModal(false);
    dispatch(
      deleteTranscriptionAssetVersion({
        assetId: asset.id,
        assetVersionId: selectedVersion.id
      })
    );
  };

  return (
    <>
      Transcript
      {!!menuItems?.length && (
        <Dropdown
          menu={{
            items: menuItems,
            onOpenChange: (openKeys) => {
              setOpenKeysSubMenu(openKeys);
            },
            onClick: onCloseMenu
          }}
          open={
            (isShowMenu && !isMdWidth) ||
            (isShowMenu && isMdWidth && !openKeysSubMenu.length)
          }
          prefixCls="media_viewer_menu"
          overlayClassName="media_viewer_transcript-drawer-menu media_viewer_mobile-overlay"
          placement="bottomRight"
          onOpenChange={(open) => {
            setIsShowMenu(open);
          }}
        >
          <Button className="media_viewer_btn b-only-icon">
            <ActionSvg className="b-white-icon" />
          </Button>
        </Dropdown>
      )}
      <ConfirmModal
        visible={isShowDeleteTranscriptionModal}
        onConfirm={onDeleteTranscription}
        onCancel={() => setIsShowDeleteTranscriptionModal(false)}
        title="Delete transcription"
        description="Are you sure you want to delete the transcription?"
      />
    </>
  );
}
